import PostApi from 'api/post';
import { FC, useEffect } from 'react';
import StringUtils from 'utils/string';
import { useMutation } from 'react-query';
import { AttachmentConvertor } from 'utils/attachments';
import { getCleanUrl, prependHttp } from 'utils/url';
import { ApiClientError } from 'api/client';
import { useToast } from 'app/hooks/useToast';
import { PostComposerFormState } from 'app/modules/posts/modules/post/components/composer/utils/postComposerFormState';
import TimeUtils from 'app/utils/time';
import { UseSetStateAction } from 'utils/hooks/useSetState';
import axios from 'axios';

type Props = {
  value: PostComposerFormState;
  onChange: UseSetStateAction<PostComposerFormState>;
  withLinkFieldOverride?: boolean;
};

const PostComposerContentLinkAppender: FC<Props> = ({
  value,
  onChange,
  withLinkFieldOverride = false,
}) => {
  const toast = useToast();
  const fetchLinkInfo = useMutation(PostApi.fetchLinkInfo, {
    onSuccess(link) {
      onChange((prev) => ({
        attachments: {
          ...prev.attachments,
          cards: [AttachmentConvertor.fromLinkInfo(link)],
        },
      }));
    },
    onError(e: ApiClientError) {
      if (e?.message === 'canceled') {
        return;
      }

      toast(e?.userMessage ?? 'Unable to retrieve link info.', 'error');
    },
  });

  useEffect(() => {
    if (!withLinkFieldOverride && !!value.attachments.cards?.[0]?.url) {
      return;
    }

    const cancelToken = axios.CancelToken.source();

    const timeout = setTimeout(() => {
      function getLastUrlInText() {
        const allUrlsInText = value.text.match(StringUtils.REGEX.URLS) || [];
        const lastUrlInText = allUrlsInText[allUrlsInText.length - 1];

        return lastUrlInText ? prependHttp(lastUrlInText) : undefined;
      }

      const lastUrlInText = getLastUrlInText();

      function getUrlToBeAddedAsLink() {
        if (lastUrlInText && value.attachments.cards.length < 2) {
          if (value.attachments.cards.length === 1) {
            const isAlreadyPresent =
              getCleanUrl(value.attachments.cards[0].url) ===
              getCleanUrl(lastUrlInText);

            const isNonLinkAttachment = !value.attachments.cards[0].url;

            if (isAlreadyPresent || isNonLinkAttachment) {
              return undefined;
            }
          }

          return lastUrlInText;
        }
      }

      const urlToBeAddedAsLink = getUrlToBeAddedAsLink();

      if (urlToBeAddedAsLink) {
        fetchLinkInfo.mutate({
          url: urlToBeAddedAsLink,
          pageId: value.page.id,
          cancelToken: cancelToken.token,
        });
      }

      const removeLinkAsOnlyAttachment =
        !lastUrlInText &&
        value.attachments.cards.length === 1 &&
        value.attachments.cards[0].url;

      if (removeLinkAsOnlyAttachment && withLinkFieldOverride) {
        onChange({
          attachments: {
            ...value.attachments,
            cards: [],
          },
        });
      }
    }, TimeUtils.calcMilliseconds.seconds(1));

    return () => {
      cancelToken.cancel('canceled');
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.text, value.attachments]);

  return null;
};

export default PostComposerContentLinkAppender;
